import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">&mdash; {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Greenwich\'s RED DfT rating guarantee my claim will succeed?',
    answer: 'No guarantee — but the borough\'s own transparency report records 59% of C-roads and 30% of B-roads in RED (poor) condition when last surveyed in 2021/22, with no published update since. The Department for Transport rates Greenwich RED overall, with RED spend and best-practice scorecards, and flags incomplete road condition data. Section 58 still turns on whether your specific defect was reasonably inspected and repaired.',
  },
  {
    question: 'What if my pothole was on an A-road?',
    answer: 'A-roads (39km — 8% of the network) are surveyed annually by Transport for London. Greenwich still needs to prove it acted on survey findings promptly, identified defects within response timeframes, and prioritised repairs appropriately — especially given the council\'s own admission of "a steady decline to the network" on principal routes.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 384km — 79.5% of Greenwich\'s 483km carriageway network. The transparency report publishes no condition breakdown for U-roads and no documented annual inspection schedule for this road class. If your incident was on a U-road, the council\'s published evidence simply does not cover your street at network level.',
  },
  {
    question: 'What if they say they are prioritising the worst roads first?',
    answer: 'Then they need to prove your road was inspected and assessed, that it did not meet their priority criteria, and that the priority system itself is reasonable given 59% of C-roads were RED when last surveyed in 2021/22. If most of the classified network already qualified as poor, "prioritising the worst" is not a narrow exception — it describes most of the surveyed network.',
  },
  {
    question: 'Why does the spending reversal (84% to 38% preventative) matter?',
    answer: 'Best practice is typically 70–80% preventative spending. Greenwich reached 84.21% preventative in 2022/23, then projected 38.43% preventative for 2025/26 — with reactive share rising to 61.57%, the highest in five years. When preventative maintenance declines and reactive repairs increase, defects form faster than the network is stabilised.',
  },
  {
    question: 'Pothole fills fell to 1,083 in 2024/25 — does that mean roads are improving?',
    answer: 'Not necessarily. Greenwich classes potholes as carriageway defects costing £200 or less to repair — larger defects are excluded from this count. Fills fell from 2,698 (2020/21) to 1,083 (2024/25) while resurfacing hit a five-year low of 3.034km, reactive spending rose to 61.57%, and the council acknowledges steady network decline. Fewer recorded fills may reflect reporting thresholds, inspection frequency, or budget constraints — not better roads.',
  },
  {
    question: 'How do I report a pothole to Royal Borough of Greenwich?',
    answer: 'Report road and pavement problems via FixMyStreet at fix.royalgreenwich.gov.uk, or through the council\'s online form at royalgreenwich.gov.uk. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function GreenwichCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Greenwich Pothole Claims | 59% of C-Roads in RED Condition | Fixtyer"
        description="Royal Borough of Greenwich: 59% of C-roads in RED when last surveyed, four-year B/C data gap, and DfT RED overall rating. 384km of U-roads with no published condition data. What this means for your Section 58 claim."
        canonical="/councils/greenwich"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Royal Borough of Greenwich', url: '/councils/greenwich' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Royal Borough of Greenwich"
        councilSlug="greenwich"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/greenwich"
        headline="Greenwich Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Greenwich</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-rose-200 text-sm">
              <strong className="text-rose-400">DfT caveat:</strong> The Department for Transport flags that
              Greenwich&apos;s 2025/26 overall, condition and best-practice scorecards are based on{' '}
              <strong className="text-white">incomplete road condition data</strong> — reflecting gaps in published
              B/C surveys and no U-road condition breakdown.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            59% of Greenwich C-Roads Were RED — Four Years Without an Update
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Royal Borough of Greenwich maintains 483km of roads. When B and C roads were last surveyed in 2021/22,{' '}
            <strong className="text-rose-400">59% of C-roads and 30% of B-roads</strong> were in RED (poor)
            condition — and no subsequent survey data has been published. The other{' '}
            <strong className="text-rose-400">384km of unclassified roads (79.5% of the network)</strong> have no
            published condition data. The DfT rates Greenwich <strong className="text-rose-400">RED</strong> overall,
            with projected 2025/26 reactive spending at <strong className="text-amber-400">61.57%</strong> — the
            highest share in five years.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">61.57%</div>
            <div className="text-white font-medium mt-2">Reactive share projected for 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              After reaching 84.21% preventative in 2022/23, Greenwich projected just 38.43% preventative for 2025/26 —
              reversing a strategy that was working while the council acknowledges &ldquo;a steady decline to the
              network&rdquo;.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            483km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Greenwich&apos;s 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">483km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">384km</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-rose-700 mt-1">79.5% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">60km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">12.4% of network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">39km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">8% — surveyed annually by TfL</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Greenwich highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">962km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway structures</td>
                  <td className="px-6 py-4 text-center font-medium">133</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">~22,000</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Drainage gullies</td>
                  <td className="px-6 py-4 text-center font-medium">~20,746</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="These surveys show that there is a steady decline to the network."
            source="Royal Borough of Greenwich Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            When 59% Is RED, Where Is the Update?
          </h2>
          <p className="text-surface-600 mb-8">
            The last available B and C road condition data (2021/22) — and nothing published since
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Data gap caveat:</strong> Greenwich&apos;s transparency report states &ldquo;yearly surveys on
              lesser classed roads resumed in early 2025&rdquo; and that it is &ldquo;awaiting data for 24-25 FY, due
              in July 2025&rdquo;. After discovering 59% of C-roads and 30% of B-roads in RED condition, no follow-up
              survey results were published for three years.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* B roads */}
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <h3 className="font-semibold text-brand-900 mb-4">B-roads (2021/22 survey)</h3>
              <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-100 border-b border-surface-200">
                      <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                      <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                      <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 text-center font-bold text-rose-600">30%</td>
                      <td className="px-4 py-2 text-center">2%</td>
                      <td className="px-4 py-2 text-center text-emerald-600">68%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-surface-600 text-sm">Nearly a third of B-roads were in poor condition at the last survey.</p>
            </div>

            {/* C roads */}
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">C-roads (2021/22 survey)</h3>
              <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-rose-100 border-b border-rose-200">
                      <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                      <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                      <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 text-center font-bold text-rose-600">59%</td>
                      <td className="px-4 py-2 text-center text-amber-600">30%</td>
                      <td className="px-4 py-2 text-center">11%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-rose-800 font-medium text-sm">
                Only 11% of C-roads were in good condition — the majority of this road class was poor or deteriorating.
              </p>
            </div>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (39km) — surveyed annually by TfL</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">19%</td>
                    <td className="px-4 py-2 text-center text-emerald-600">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center text-emerald-600">72%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024/25</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>Not yet published</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Principal roads remain predominantly green-rated — but the council acknowledges steady decline even on A-roads.
              TfL maintains red routes separately; Greenwich manages other public highways in the borough.
            </p>
          </div>

          {/* U roads blind spot */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (384km) — no published condition data</h3>
            <p className="text-rose-800 text-sm mb-4">
              The transparency report specifies no documented annual inspection schedule for U-roads and publishes no
              red/amber/green condition breakdown for this road class. Residential streets, estate roads and local access
              routes — where most Greenwich residents drive daily — sit outside the published condition evidence base.
            </p>
            <p className="text-rose-800 font-medium text-sm">
              If 59% of surveyed C-roads were RED when last checked, the condition of the unsurveyed 79.5% remains
              undocumented — a structural gap the DfT&apos;s incomplete-data flag reflects.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            RED spend scorecard — preventative share reversed from 84% to 38% in three years
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Greenwich highway maintenance spending split 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center text-emerald-400">76.76%</td>
                  <td className="px-4 py-3 text-center">23.24%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center text-emerald-400">77.15%</td>
                  <td className="px-4 py-3 text-center">22.85%</td>
                </tr>
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2022/23</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">84.21%</td>
                  <td className="px-4 py-3 text-center">15.79%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center text-amber-400">58.53%</td>
                  <td className="px-4 py-3 text-center">41.47%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center text-amber-400">53.76%</td>
                  <td className="px-4 py-3 text-center">46.24%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">38.43%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">61.57%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-300 px-4 py-3 border-b border-brand-800">
              Capital spending (£000s) — three years at zero
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-2 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-300">DfT allocation</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-300">Capital spend</th>
                  <th className="px-4 py-2 text-center font-semibold text-brand-300">km resurfaced</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-2">2020/21</td>
                  <td className="px-4 py-2 text-center">£0</td>
                  <td className="px-4 py-2 text-center">£60</td>
                  <td className="px-4 py-2 text-center">6.76</td>
                </tr>
                <tr className="bg-rose-900/20">
                  <td className="px-4 py-2">2021/22</td>
                  <td className="px-4 py-2 text-center">£0</td>
                  <td className="px-4 py-2 text-center font-bold text-rose-400">£0</td>
                  <td className="px-4 py-2 text-center">6.32</td>
                </tr>
                <tr className="bg-rose-900/20">
                  <td className="px-4 py-2">2022/23</td>
                  <td className="px-4 py-2 text-center">£0</td>
                  <td className="px-4 py-2 text-center font-bold text-rose-400">£0</td>
                  <td className="px-4 py-2 text-center">5.95</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">2023/24</td>
                  <td className="px-4 py-2 text-center">£250</td>
                  <td className="px-4 py-2 text-center text-emerald-400">£800</td>
                  <td className="px-4 py-2 text-center">6.02</td>
                </tr>
                <tr>
                  <td className="px-4 py-2">2024/25</td>
                  <td className="px-4 py-2 text-center">£250</td>
                  <td className="px-4 py-2 text-center">£800</td>
                  <td className="px-4 py-2 text-center font-bold text-rose-400">3.034</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-2 text-center">£813</td>
                  <td className="px-4 py-2 text-center">£800</td>
                  <td className="px-4 py-2 text-center">4.8 (planned)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is RED</h3>
              <p className="text-brand-200 text-sm">
                Three consecutive years of zero capital investment (2021/22–2022/23), total 2024/25 highways spend of
                £2.83m across 483km of roads (£5,858/km), and projected capital spend of £800k against a DfT allocation
                of £813k — barely meeting the grant after years of under-investment.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">The reversal in numbers</h3>
              <p className="text-brand-200 text-sm">
                Greenwich reached 84.21% preventative in 2022/23 — then projected 38.43% for 2025/26. The council states
                a portion of reactive budget was reallocated to resurfacing, but the spending data shows reactive share
                rising to its highest level in five years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Fewer Recorded Pothole Fills — Not Fewer Defects
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled from Greenwich&apos;s transparency report — defects costing £200 or less to repair
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Greenwich estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,698</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,792</td>
                  <td className="px-6 py-4 text-center text-amber-600">&minus;33.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,543</td>
                  <td className="px-6 py-4 text-center text-surface-500">&minus;5.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,161</td>
                  <td className="px-6 py-4 text-center text-amber-600">&minus;19.9%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,083</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">&minus;59.9%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Royal Greenwich classes potholes as any carriageway defect costing equal or less than £200 to repair."
              source="Royal Borough of Greenwich Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Resurfacing at 0.6% of the network</h3>
              <p className="text-surface-600 text-sm">
                Greenwich resurfaced 3.034km in 2024/25 — 0.6% of its 483km network, down from 6.76km in 2020/21 (a
                55% decline). At the 2025/26 planned rate of 4.8km (1% of the network), a full network refresh would
                take roughly 100 years — far beyond typical 15–20 year surface lifespans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Greenwich says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency (transparency report)</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>&bull; <strong>A roads:</strong> annual condition surveys by Transport for London</li>
                <li>&bull; <strong>B and C roads:</strong> last surveyed 2021/22; yearly surveys resumed early 2025</li>
                <li>&bull; <strong>U roads:</strong> no documented annual inspection schedule in transparency report</li>
                <li>&bull; <strong>2024/25 condition data:</strong> awaited, due July 2025</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What &ldquo;reasonable&rdquo; means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Under Section 58 of the Highways Act 1980, Greenwich must show a reasonable system for inspecting and
                repairing the specific defect — not merely cite budget constraints. That requires documented inspections,
                risk-based prioritisation, timely repairs, and records proving the system was followed.
              </p>
              <p className="text-surface-600 text-sm">
                The burden of proof is on the council: when your road was last inspected, what was found, and why the
                pothole was not repaired before your incident.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Evidence working against Greenwich</h3>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>&bull; Acknowledged &ldquo;steady decline to the network&rdquo;</li>
              <li>&bull; 59% of C-roads in RED when last surveyed (2021/22)</li>
              <li>&bull; Four years without published B/C condition data</li>
              <li>&bull; Zero capital investment 2021/22 and 2022/23</li>
              <li>&bull; Reactive spending projected at 61.57% for 2025/26</li>
              <li>&bull; Resurfacing declined 55% (6.76km to 3.034km)</li>
              <li>&bull; 79.5% of network (U-roads) with no published condition data</li>
              <li>&bull; DfT incomplete road condition data flag on scorecards</li>
            </ul>
          </div>

          <QuoteCard
            quote="Since 2020, the impact of rising construction and material costs has reduced the number of roads that can be resurfaced each year within existing budgets."
            source="Royal Borough of Greenwich Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Greenwich Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On network decline</h3>
              <QuoteCard
                quote="These surveys show that there is a steady decline to the network."
                source="Royal Borough of Greenwich Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On survey resumption</h3>
              <QuoteCard
                quote="Yearly surveys on lesser classed roads resumed in early 2025."
                source="Royal Borough of Greenwich Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On budget reallocation</h3>
              <QuoteCard
                quote="A portion of the reactive maintenance budget has been reallocated to support additional resurfacing work."
                source="Royal Borough of Greenwich Local Highways Maintenance Transparency Report 2025"
              />
              <p className="text-surface-600 text-sm mt-3">
                The spending data tells a different story: preventative share fell from 84.21% (2022/23) to a projected
                38.43% (2025/26), with reactive share at its highest in five years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Greenwich&apos;s published data creates structural weaknesses — but your claim still
            needs specific evidence
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads predominantly green-rated under annual TfL surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative spending reached 84.21% in 2022/23 — showing capacity when funded
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend resumed from 2023/24 (£800k annually)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C surveys resumed in early 2025
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                On A-roads with recent TfL survey data, expect a more prepared Section 58 defence.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall, spend and best-practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 384km of U-roads — 79.5% of network — with no published condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 59% of C-roads RED at last survey; four-year data gap
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Zero capital investment 2021/22–2022/23 during network decline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive spending projected at 61.57%; resurfacing at five-year low
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT incomplete road condition data flag
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with RED scorecards and a four-year condition data gap, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road type</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>&bull; Prior reports of the same pothole (FixMyStreet, council portal) — proof of actual notice</li>
              <li>&bull; Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>&bull; Road class — on a U-road, the absence of published condition data is your strongest structural argument</li>
              <li>&bull; Whether the council can produce inspection records for your street, not just network-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from
              Greenwich&apos;s own transparency data — including the DfT&apos;s incomplete-data caveat — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Royal Borough of Greenwich
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack. The council inspects reported problems within five working
            days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://fix.royalgreenwich.gov.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
            >
              Report via FixMyStreet — fix.royalgreenwich.gov.uk
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <a
              href="https://www.royalgreenwich.gov.uk/parking-transport-and-streets/report-issues-street/report-road-or-pavement-problem"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-900 font-semibold rounded-xl border border-surface-300 hover:bg-surface-50 transition-colors"
            >
              Council online form — royalgreenwich.gov.uk
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Greenwich?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated borough with a four-year data gap demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>&bull; Submit photos and invoices</li>
                <li>&bull; Use generic template letter</li>
                <li>&bull; No U-road data-gap argument</li>
                <li>&bull; No prior-report search</li>
                <li>&bull; No DfT incomplete-data citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 59% C-roads RED condition documented</li>
                <li>✅ Four-year B/C survey gap argued</li>
                <li>✅ 79.5% U-road evidence gap cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Greenwich</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=greenwich"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Greenwich Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.royalgreenwich.gov.uk/parking-transport-and-streets/policies-and-reports-about-parking-transport-and-streets/local"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Royal Borough of Greenwich Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
